export function normalizeTr(str) {
  return (str || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[İIı]/g, 'i')
    .toLowerCase();
}

export function isPathActive(itemPath, pathname) {
  if (!itemPath) return false;
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
}

export function filterAccessible(nodes, hasAccess) {
  const out = [];
  for (const node of nodes) {
    if (node.children) {
      if (!hasAccess(node)) continue;          // gate the group (moduleGated/privileges)
      const kids = filterAccessible(node.children, hasAccess);
      if (kids.length > 0) out.push({ ...node, children: kids });
    } else if (hasAccess(node)) {
      out.push(node);
    }
  }
  return out;
}

// En spesifik eşleşen yaprağın kök→yaprak key izi. Exact > en uzun prefix.
export function findActiveTrail(nodes, pathname) {
  let best = null; // { trail, len, exact }
  const walk = (list, acc) => {
    for (const node of list) {
      if (node.children) {
        walk(node.children, [...acc, node.key]);
      } else if (node.path) {
        const exact = pathname === node.path;
        const prefix = pathname.startsWith(node.path + '/');
        if (exact || prefix) {
          const cand = { trail: [...acc, node.key], len: node.path.length, exact };
          if (!best || (exact && !best.exact) ||
              (exact === best.exact && cand.len > best.len)) best = cand;
        }
      }
    }
  };
  walk(nodes, []);
  return best ? best.trail : [];
}

// SidebarMenu.hasAccess'in moduleGated dallanması için modül-kodu çözümü:
// açık `module` alanı VARSA o kullanılır (lockGated'in tercih ettiği aynı
// kaynak — bkz. aşağıdaki lockGated); yoksa geriye-dönük uyumluluk için
// privileges[0]'dan çıkarıma düşer. Salt çıkarıma güvenmek kırılgan: bir
// moduleGated düğümün privileges'i [] olduğunda (ör. bir self-servis fallback
// çocuğu barındıran ama kendisi boş privilege'lı bir grup) moduleCode
// sessizce undefined'a düşer ve modül-abonelik kapısı hiç uygulanmaz (Faz A
// ledger T9). Explicit `module` alanı taşıyan düğümler (bugün PM ve TD
// grupları) bu sınıf hatasına artık kapalı; `module` alanını henüz
// taşımayan diğer moduleGated grupları (leave-management, time-attendance,
// vb.) inference fallback'iyle değişmeden çalışmaya devam eder.
export function moduleGateCode(item) {
  return item.module || item.privileges?.[0]?.split('.')[0];
}

// Modül Bazlı Lisanslama, Düzeltme turu 2 — moduleGated bir öğe için ARTIK iki
// kapı var: kiracının modülü var mı (hasModule), VE kullanıcının o modülde
// koltuğu var mı (hasSeat). İkincisi olmadan bir kurumsal admin joker
// ayrıcalığıyla menüde görüp tıklayınca 403 alan bir öğe doğardı — backend
// ölçümlü modül uçlarına koltuk kapısı taktığında bu sınıf hata canlanırdı.
//
// `hasSeat` ölçümsüz/varsayılan modüllerde VE `seats` alanı henüz olmayan eski
// oturumlarda HER ZAMAN true döner (bkz. resolveHasSeat) — bu fonksiyon o
// davranışı DEĞİŞTİRMEZ, yalnız SidebarMenu.hasAccess'in onu ÇAĞIRMASINI sağlar.
// moduleCode çözülemiyorsa (moduleGateCode undefined) eskisi gibi kapı hiç
// uygulanmaz — true döner, çağıran genel ayrıcalık kontrollerine devam eder.
export function moduleSubscriptionGate(item, { hasModule, hasSeat }) {
  if (!item.moduleGated) return true;
  const moduleCode = moduleGateCode(item);
  if (!moduleCode) return true;
  if (!hasModule(moduleCode)) return false;
  return hasSeat(moduleCode);
}

// Görev 26 (B5) — enterpriseAdmin (İK) kapısı. Sunucu tarafı YALNIZ aktif
// enterpriseAdmin'e veri dönen yüzeyler (PIP süreçleri, sonuç itirazları —
// PMB-08'de daraltıldı) menüde de yalnız İK'ya görünmelidir: PM.Read'i olan ama
// İK olmayan bir kullanıcı aksi halde menüyü görüp KALICI OLARAK BOŞ bir
// listeye düşer ("kapı doğru, menü yalancı"). Sayfa-içi İK ayrımının
// (CompCyclesPage / DelegationsTab: canWrite('PM') && isEnterpriseAdmin) menü
// karşılığı.
//
// SAF fonksiyon (lockGated emsali — testleri menuTree.test.js'te):
// SidebarMenu.hasAccess bunu superAdmin dalından SONRA çağırır, o yüzden
// superAdmin buraya hiç gelmez; yine de savunmacı olarak burada da muaf tutulur
// (bu fonksiyon tek başına doğru olmalı — "tüm kontroller baypas" kuralı).
export function enterpriseAdminGate(item, { isEnterpriseAdmin, isSuperAdmin }) {
  if (!item?.requiresEnterpriseAdmin) return true;
  return Boolean(isSuperAdmin) || Boolean(isEnterpriseAdmin);
}

// İK rolü ayrımı (2026-10-28) — `requiresHr`: sunucu tarafı yalnız İK'ya (HrActorRules)
// veri dönen yüzeyler. `enterpriseAdminGate`'in İK ikizi: İK artık Şirket Yöneticisi
// demek DEĞİL ("İK Rolü" işaretli rolün sahibi; kiracı İK rolü kullanmıyorsa Şirket
// Yöneticisi — `isHr` AuthContext'te çözülür). `requiresEnterpriseAdmin` gerçekten
// yönetici yüzeyleri (SSO gibi) için kalır.
export function hrGate(item, { isHr, isSuperAdmin }) {
  if (!item?.requiresHr) return true;
  return Boolean(isSuperAdmin) || Boolean(isHr);
}

// Faz 3 / PM-01 + PM-06 — OR-kapısı: "listelenen ayrıcalıklardan biri VEYA
// sunucudan gelen şu kullanıcı bayrağı". `requiresUserFlag` ile KARIŞTIRILMAZ:
//   • requiresUserFlag = AND (bayrak yoksa öğe TAMAMEN gizli — "İşten Ayrılışım")
//   • orUserFlag      = OR  (ayrıcalık yoksa bayrak KURTARIR)
// Gerekçe: PM'de karar yetkisi artık modül grantından değil İLİŞKİDEN geliyor
// (PeriodGoalController'ın yönetici bölgesi + CompCycleMyTeamController), yani
// PM.Read'i olmayan bir amir "Hedef Yönetimi"/"Ücret Artış Turu" ekranlarını
// kullanabiliyor ama menüde göremiyordu. `privileges: []` yapmak YANLIŞ çözüm
// olurdu: astı olmayan her çalışana kalıcı olarak boş açılan bir menü girdisi
// üretirdi (TD-31'in "9-Kutum" kusurunun aynısı).
//
// SAF fonksiyon (enterpriseAdminGate/lockGated emsali) — testleri menuTree.test.js'te.
// Analitik Plan 3 (spec §5.4) — "İK VEYA kullanıcı bayrağı" kapısı: PM raporları İK'ya (isHr /
// superAdmin) ya da astı olana açıktır; PM.Read TEK BAŞINA yetki değildir (backend 403 döner). `orUserFlag`
// ayrıcalığa EK yol açar (PM.Read'li astsız kişi yine görürdü); bu kapı ayrıcalığa HİÇ bakmaz.
export function hrOrUserFlagGate(item, { isHr, isSuperAdmin }, user) {
  if (!item?.hrOrUserFlag) return true;
  return Boolean(isSuperAdmin) || Boolean(isHr) || Boolean(user?.[item.hrOrUserFlag]);
}

// Bilinçli çift yol tekilleştirmesi (inceleme M13b, AKM POC §5.4): /holiday-calendars hem superAdmin'in
// küresel şablon girişi (Sistem altında, privileges ['superAdmin']) hem kiracının şirket takvimleri girişi
// (Organizasyon altında, EM.Write). superAdmin "tüm kontroller baypas" dalından geçtiği için İKİSİNİ de
// görüyordu. `hiddenForSuperAdmin` taşıyan öğe superAdmin'den gizlenir — superAdmin aynı sayfayı kendi
// platform girişinden açar; superAdmin olmayan herkes zaten yalnız kiracı girişini görür
// (privileges ['superAdmin'] olan öğe onlara kapalıdır). SidebarMenu.hasAccess bunu superAdmin baypasından
// ÖNCE çağırır. SAF fonksiyon — testleri menuTree.test.js ve MenuConfig.test.js'te.
export function superAdminHiddenGate(item, { isSuperAdmin }) {
  return !(isSuperAdmin && item?.hiddenForSuperAdmin);
}

// `orUserFlag` tek bayrak ya da bayrak DİZİSİ olabilir (dizide biri yeterli). Hedef onay akışı
// (2026-10-28): Ekip Hedefleri hem astı olana (`hasSubordinates`) hem de bir Hedef onay akışında
// adım onaycısı olana (`hasGoalFlowApprovals`) açılır — onaycının astı olmayabilir.
export function userFlagGate(item, user) {
  if (!item?.orUserFlag) return false;
  const flags = Array.isArray(item.orUserFlag) ? item.orUserFlag : [item.orUserFlag];
  return flags.some((flag) => Boolean(user?.[flag]));
}

// Kilitli-görünür deseni (yalnız lockedWhenUnsubscribed taşıyan gruplar; bugün
// sadece Yetenek & Gelişim): tenant modüle abone değilse grup, children'sız tek
// "kilitli" yaprağa dönüşür — privileges [] olduğundan herkes görür, tıklama
// /talent/upsell tanıtımını açar. superAdmin her zaman kilitsiz görür.
export function lockGated(nodes, { hasModule, isSuperAdmin }) {
  return nodes.map((node) => {
    if (node.lockedWhenUnsubscribed && !isSuperAdmin) {
      // Modül kodu explicit `module` alanından okunur — privileges'tan çıkarım
      // (ör. node.privileges?.[0]?.split('.')[0]) yapılmaz: privileges boş
      // dizi olduğunda bu çıkarım sessizce undefined'a düşer, kilit hiç
      // uygulanmaz ve grup kapısız kalır (bkz. menuTree.test.js "Bulgu 1").
      // `module` alanı yoksa kilit UYGULANMAZ ama düğüm de burada
      // sessizce açılmaz — normal moduleGated/hasAccess kapısına düşmeye
      // devam eder (aşağıdaki fallthrough).
      const moduleCode = node.module;
      if (moduleCode && !hasModule(moduleCode)) {
        // Destructure-to-omit: children/moduleGated/lockedWhenUnsubscribed/module
        // are intentionally dropped from the locked leaf, not read here. `module`
        // must be dropped too — otherwise downstream hasAccess's own module gate
        // (`item.module && !hasModule(item.module)`) would hide the locked leaf
        // entirely instead of showing the upsell.
        // eslint-disable-next-line no-unused-vars
        const { children, moduleGated, lockedWhenUnsubscribed, module, ...rest } = node;
        return { ...rest, privileges: [], locked: true, path: '/talent/upsell' };
      }
    }
    return node.children ? { ...node, children: lockGated(node.children, { hasModule, isSuperAdmin }) } : node;
  });
}

export function searchMenu(nodes, query, label) {
  const q = normalizeTr(query);
  if (!q) return nodes;
  const filt = (list) => {
    const out = [];
    for (const node of list) {
      if (node.children) {
        const kids = filt(node.children);
        if (kids.length > 0 || normalizeTr(label(node)).includes(q)) {
          out.push({ ...node, children: kids.length ? kids : node.children });
        }
      } else if (normalizeTr(label(node)).includes(q)) {
        out.push(node);
      }
    }
    return out;
  };
  return filt(nodes);
}
